import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import {
  Breadcrumb,
  Button,
  Form,
  Input,
  Select,
  Tag,
  Typography,
  List,
  Space,
  message,
  Spin,
  Row,
  Col,
} from 'antd';
import {
  getPrompt,
  createPrompt,
  updatePrompt,
  getPromptBindings,
  listTestCases,
  createTestCase,
  runTestCase,
  listPromptComments,
} from '../services/api/prompts';
import PromptEditor from '../components/prompt/PromptEditor';
import PromptDiffView from '../components/prompt/PromptDiffView';
import SettingsField, { SettingsBlock, SettingsPanel } from '../components/settings/SettingsField';
import './AddAssistant.css';

const { Text } = Typography;

export default function PromptDetail() {
  const { promptId } = useParams();
  const isNew = promptId === 'new';
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(!isNew);
  const [prompt, setPrompt] = useState(null);
  const [body, setBody] = useState('');
  const [bindings, setBindings] = useState([]);
  const [testCases, setTestCases] = useState([]);
  const [testResults, setTestResults] = useState({});
  const [comments, setComments] = useState([]);
  const [newTestName, setNewTestName] = useState('');
  const [newTestInput, setNewTestInput] = useState('');

  const load = useCallback(async () => {
    if (isNew || !keycloak?.token) return;
    setLoading(true);
    try {
      const data = await getPrompt(keycloak, promptId);
      setPrompt(data);
      setBody(data.draft_body || '');
      form.setFieldsValue({
        name: data.name,
        slug: data.slug,
        description: data.description,
        kind: data.kind,
      });
      const b = await getPromptBindings(keycloak, promptId);
      setBindings(b);
      const tc = await listTestCases(keycloak, promptId);
      setTestCases(tc);
      const cm = await listPromptComments(keycloak, promptId);
      setComments(cm);
    } catch (e) {
      message.error(e.message || 'Failed to load prompt');
    } finally {
      setLoading(false);
    }
  }, [isNew, keycloak, promptId, form]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSaveMeta = async () => {
    const values = await form.validateFields();
    try {
      if (isNew) {
        const created = await createPrompt(keycloak, { ...values, body });
        message.success('Prompt created');
        navigate(`/prompts/${created.id}`, { replace: true });
      } else {
        await updatePrompt(keycloak, promptId, values);
        message.success('Saved');
        load();
      }
    } catch (e) {
      message.error(e.message || 'Save failed');
    }
  };

  const handleAddTestCase = async () => {
    if (!newTestName || !newTestInput) return;
    try {
      await createTestCase(keycloak, promptId, { name: newTestName, input_text: newTestInput });
      setNewTestName('');
      setNewTestInput('');
      load();
    } catch (e) {
      message.error(e.message || 'Failed to add test case');
    }
  };

  const handleRunTests = async () => {
    const results = {};
    for (const tc of testCases) {
      try {
        results[tc.id] = await runTestCase(keycloak, promptId, tc.id, {});
      } catch (e) {
        results[tc.id] = { error: e.message };
      }
    }
    setTestResults(results);
  };

  if (loading) {
    return (
      <div className="page-container" style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="page-container add-assistant-page">
      <div className="add-assistant-form-panel is-single">
        <div className="add-assistant-form-inner">
          <div className="settings-main is-full">
            <div className="add-assistant-page-intro">
              <Breadcrumb
                className="add-assistant-breadcrumb"
                items={[
                  { title: <Link to="/prompts">Prompts</Link> },
                  { title: isNew ? 'New prompt' : prompt?.name },
                ]}
              />
              <h1 className="add-assistant-page-title">{isNew ? 'New prompt' : 'Edit prompt'}</h1>
              <p className="add-assistant-page-subtitle">
                {isNew
                  ? 'Name the prompt and write the first version.'
                  : 'Update the name, kind, and draft body.'}
              </p>
            </div>

            <Form form={form} layout="vertical" initialValues={{ kind: 'instance' }}>
              <SettingsPanel>
                <SettingsField label="Name" help="Shown in the prompt library.">
                  <Form.Item name="name" rules={[{ required: true, message: 'Enter a name' }]} style={{ marginBottom: 0, width: '100%' }}>
                    <Input placeholder="Support reply" />
                  </Form.Item>
                </SettingsField>
                <SettingsField label="Slug" help="Used as {{lib:slug}} inside other templates.">
                  <Form.Item name="slug" style={{ marginBottom: 0, width: '100%' }}>
                    <Input placeholder="support-reply" />
                  </Form.Item>
                </SettingsField>
                <SettingsField label="Kind" help="An instance is one prompt. A template is reused by reference.">
                  <Form.Item name="kind" style={{ marginBottom: 0, width: '100%' }}>
                    <Select
                      options={[
                        { value: 'instance', label: 'Instance' },
                        { value: 'template', label: 'Template' },
                      ]}
                    />
                  </Form.Item>
                </SettingsField>
                <SettingsField label="Description" help="A short note on when to use this prompt.">
                  <Form.Item name="description" style={{ marginBottom: 0, width: '100%' }}>
                    <Input.TextArea rows={2} placeholder="Reply tone for billing questions" />
                  </Form.Item>
                </SettingsField>
                <SettingsField label="Body" help="The text this prompt sends to the model." stack>
                  <PromptEditor
                    value={body}
                    onChange={setBody}
                    features={{
                      history: !isNew,
                      assist: true,
                      library: true,
                      publish: !isNew,
                      testPane: !isNew,
                      compareAnyTwo: true,
                      rollback: true,
                    }}
                    onPublished={load}
                  />
                </SettingsField>
              </SettingsPanel>
              <div className="add-assistant-form-footer">
                <Button type="primary" onClick={handleSaveMeta}>
                  {isNew ? 'Create' : 'Save metadata'}
                </Button>
              </div>
            </Form>

            {!isNew && (
              <SettingsBlock title="Tests">
                <SettingsPanel padded>
                  <p className="settings-page-desc" style={{ marginBottom: 12 }}>
                    Saved example inputs. Compare the draft with the live version.
                  </p>
                  <div className="settings-field-stack" style={{ alignItems: 'stretch' }}>
                    <Input placeholder="Test name" value={newTestName} onChange={(e) => setNewTestName(e.target.value)} />
                    <Input.TextArea
                      placeholder="Sample user message"
                      value={newTestInput}
                      onChange={(e) => setNewTestInput(e.target.value)}
                      rows={2}
                    />
                    <Space>
                      <Button onClick={handleAddTestCase}>Add test case</Button>
                      <Button type="primary" onClick={handleRunTests} disabled={!testCases.length}>
                        Run all tests
                      </Button>
                    </Space>
                  </div>
                  {testCases.map((tc) => {
                    const r = testResults[tc.id];
                    return (
                      <div key={tc.id} className="settings-field-row is-stack">
                        <div className="settings-field-name">{tc.name}</div>
                        <Text type="secondary">Input: {tc.input_text}</Text>
                        {r && !r.error && (
                          <Row gutter={16}>
                            <Col span={12}>
                              <Text strong>Live</Text>
                              <div className="prompt-test-output">{r.live_output}</div>
                            </Col>
                            <Col span={12}>
                              <Text strong>Draft</Text>
                              <div className="prompt-test-output">{r.draft_output}</div>
                            </Col>
                          </Row>
                        )}
                        {r?.error && <Text type="danger">{r.error}</Text>}
                      </div>
                    );
                  })}
                </SettingsPanel>
              </SettingsBlock>
            )}

            {!isNew && (
              <SettingsBlock title="Usage">
                <SettingsPanel>
                  <SettingsField label="Used in" help="Surfaces that reference this prompt." stack>
                    {bindings.length === 0 ? (
                      <Text type="secondary">Not linked to any surface yet</Text>
                    ) : (
                      <List
                        size="small"
                        dataSource={bindings}
                        renderItem={(b) => (
                          <List.Item>
                            <Space>
                              <Tag>{b.target_type}</Tag>
                              <Text>{b.target_name || `#${b.target_id}`}</Text>
                            </Space>
                          </List.Item>
                        )}
                      />
                    )}
                  </SettingsField>
                  {prompt?.live_version && (
                    <SettingsField label="Live version" help="The version currently published.">
                      <span className="settings-field-name">
                        v{prompt.live_version.version_number}
                        {prompt.live_version.label ? ` ${prompt.live_version.label}` : ''}
                      </span>
                    </SettingsField>
                  )}
                  {comments.length > 0 && (
                    <SettingsField label="Suggestions" help="Comments left on this prompt." stack>
                      <List
                        size="small"
                        dataSource={comments}
                        renderItem={(c) => (
                          <List.Item>
                            <div>
                              <Text>{c.body}</Text>
                              {c.suggested_text && (
                                <div style={{ marginTop: 4 }}>
                                  <PromptDiffView oldText="" newText={c.suggested_text} />
                                </div>
                              )}
                            </div>
                          </List.Item>
                        )}
                      />
                    </SettingsField>
                  )}
                </SettingsPanel>
              </SettingsBlock>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
